import React from "react";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { Form, Switch, Spin } from "antd";
import { PromotionModalProps, PromotionFormData } from "./types";
import { useTheme } from "../../context/ThemeContext";
import {
  StyledModal,
  ModalFooter,
  CloseButton,
  SubmitButton,
  FullWidthSpace,
  FullWidthInputNumber,
  FullWidthDatePicker,
  DateFormItemWrapper,
  HelperText,
  StyledInput,
  StyledTextArea,
  GenerateButton,
} from "./Styles";
import CustomSelector from "../../commonComponents/Selector";
import { SelectOption } from "../../commonComponents/Selector/types";
import dayjs, { Dayjs } from "dayjs";


const TYPE_OPTIONS: SelectOption[] = [
  { value: "percentage", label: "Percentage Discount" },
  { value: "fixed", label: "Fixed Amount Discount" },
  { value: "buy_one_get_one", label: "Buy 1 Get 1 Free" },
];

const PromotionModal: React.FC<PromotionModalProps> = ({
  isOpen,
  onClose,
  selectedPromotion,
  formData,
  setFormData,
  onSave,
  onGenerateCode,
  isLoading = false,
  isSaving = false,
}) => {
  const { isDarkMode } = useTheme();
  const styledTheme = { mode: isDarkMode ? "dark" : "light" } as DefaultTheme;
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        ...formData,
        startDate: formData.startDate ? dayjs(formData.startDate) : null,
        endDate: formData.endDate ? dayjs(formData.endDate) : null,
      });
    }
  }, [isOpen, formData, form]);

  const handleFormChange = (field: keyof PromotionFormData, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleDateChange = (field: "startDate" | "endDate", date: Dayjs | null) => {
    setFormData({
      ...formData,
      [field]: date ? date.format("YYYY-MM-DD") : "",
    });
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      onSave();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <StyledThemeProvider theme={styledTheme}>
      <StyledModal
      title={selectedPromotion ? "Edit Promotion" : "Create New Promotion"}
      open={isOpen}
      onCancel={onClose}
      width={800}
      zIndex={2000}
      destroyOnClose
      footer={
        <ModalFooter>
          <CloseButton onClick={onClose} disabled={isSaving}>
            Cancel
          </CloseButton>
          <SubmitButton
            type="primary"
            onClick={handleSave}
            loading={isSaving}
          >
            {selectedPromotion ? "Update" : "Create"}
          </SubmitButton>
        </ModalFooter>
      }
    >
      <Spin spinning={isLoading} tip="Loading promotion details...">
        <Form form={form} layout="vertical">
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <StyledInput
              placeholder="e.g., Summer Sale 2025"
              value={formData.title}
              onChange={(e) => handleFormChange("title", e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <StyledTextArea
              rows={3}
              placeholder="Describe your promotion..."
              value={formData.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Promo Code"
            name="code"
            rules={[{ required: true, message: "Please enter a promo code" }]}
          >
            <div>
              <StyledInput
                placeholder="SUMMER20"
                value={formData.code}
                onChange={(e) => handleFormChange("code", e.target.value.toUpperCase())}
                style={{ fontFamily: "monospace", width: "100%", marginBottom: "8px" }}
              />
              <GenerateButton onClick={onGenerateCode}>
                Generate
              </GenerateButton>
            </div>
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: "Please select a type" }]}
          >
            <CustomSelector
              value={formData.type}
              onChange={(value) => handleFormChange("type", value as "percentage" | "fixed" | "buy_one_get_one")}
              options={TYPE_OPTIONS}
              placeholder="Select type"
              height={38}
              width={'100%'}
              borderRadius={8}
            />
          </Form.Item>

          {formData.type !== "buy_one_get_one" && (
            <>
              <Form.Item
                label={
                  formData.type === "percentage"
                    ? "Discount Percentage"
                    : "Discount Amount"
                }
                name="discountValue"
                rules={[{ required: true, message: "Please enter discount value" }]}
              >
                <FullWidthInputNumber
                  min={0}
                  max={formData.type === "percentage" ? 100 : 10000}
                  value={formData.discountValue}
                  onChange={(value) => handleFormChange("discountValue", value || 0)}
                />
              </Form.Item>

              {formData.type === "percentage" && (
                <Form.Item label="Maximum Discount ($)" name="maxDiscount">
                  <FullWidthInputNumber
                    min={0}
                    value={formData.maxDiscount}
                    onChange={(value) => handleFormChange("maxDiscount", value || 0)}
                  />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item label="Minimum Order Amount ($)" name="minOrderAmount">
            <FullWidthInputNumber
              min={0}
              value={formData.minOrderAmount}
              onChange={(value) => handleFormChange("minOrderAmount", value || 0)}
            />
          </Form.Item>

          <FullWidthSpace>
            <DateFormItemWrapper
              label="Start Date"
              name="startDate"
              rules={[{ required: true, message: "Please select start date" }]}
            >
              <FullWidthDatePicker
                value={formData.startDate ? dayjs(formData.startDate) : null}
                onChange={(date: Dayjs | null) => handleDateChange("startDate", date)}
              />
            </DateFormItemWrapper>

            <DateFormItemWrapper
              label="End Date"
              name="endDate"
              rules={[{ required: true, message: "Please select end date" }]}
            >
              <FullWidthDatePicker
                value={formData.endDate ? dayjs(formData.endDate) : null}
                onChange={(date: Dayjs | null) => handleDateChange("endDate", date)}
              />
            </DateFormItemWrapper>
          </FullWidthSpace>

          <Form.Item label="Usage Limit" name="usageLimit">
            <FullWidthInputNumber
              min={0}
              value={formData.usageLimit}
              onChange={(value) => handleFormChange("usageLimit", value || 0)}
              placeholder="0 = Unlimited"
            />
            <HelperText>
              Leave as 0 for unlimited usage
            </HelperText>
          </Form.Item>

          <Form.Item label="Active Status" name="isActive" valuePropName="checked">
            <Switch
              checked={formData.isActive}
              onChange={(checked) => handleFormChange("isActive", checked)}
            />
          </Form.Item>
        </Form>
      </Spin>
    </StyledModal>
    </StyledThemeProvider>
  );
};

export default PromotionModal;

